import React from "react";
import { useTranslation } from "react-i18next";
import RefSiteFooter from "./RefSiteFooter";

const TERMS_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rt-ink: #163A5A; --rt-ink2: #3D6A8C; --rt-ink3: #5E87A3;
    --rt-sky: #1A63A5; --rt-mint: #2AB6E8;
    --rt-secondary: #2AB6E8;
    --rt-secondary-soft: #D9F4FC;
    --rt-secondary-muted: rgba(42,182,232,0.18);
    --rt-sky-light: #E8F4FC;
    --rt-white: #FFFFFF; --rt-surface: #F5F9FC;
    --rt-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; --rt-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }
  .rt-page { font-family: var(--rt-ff-body); color: var(--rt-ink); background: var(--rt-white); line-height: 1.6; }

  /* HERO */
  .rt-hero {
    text-align: center; padding: 54px 40px 0px;
    background: linear-gradient(180deg, #E8F4FC 0%, var(--rt-secondary-soft) 35%, #fff 100%);
    position: relative; overflow: hidden;
  }
  .rt-hero::before {
    content: ''; position: absolute; top: -150px; left: 50%; transform: translateX(-50%);
    width: 900px; height: 500px;
    background: radial-gradient(ellipse, rgba(26,99,165,.08) 0%, transparent 65%);
    pointer-events: none;
  }
  .rt-hero-content { position: relative; z-index: 1; }
  .rt-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, var(--rt-sky-light) 0%, var(--rt-secondary-soft) 100%);
    color: var(--rt-sky); border: 1px solid var(--rt-secondary-muted);
    font-size: 12px; font-weight: 600; padding: 8px 16px;
    border-radius: 100px; margin-bottom: 24px; letter-spacing: .06em;
    text-transform: uppercase;
    box-shadow: 0 4px 12px rgba(42,182,232,0.1);
  }
  .rt-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--rt-secondary); animation: rt-pulse 2s infinite; }
  @keyframes rt-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(0.8)} }
  .rt-h1 { font-family: var(--rt-ff-head); font-size: 60px; font-weight: 800; letter-spacing: -.02em; line-height: 1.05; margin-bottom: 20px; }
  .rt-h1-grad { background: linear-gradient(135deg, var(--rt-sky), var(--rt-mint)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .rt-last-updated { font-size: 14px; color: var(--rt-ink3); font-weight: 500; }

  /* CONTENT */
  .rt-content-wrap { padding: 48px 24px; max-width: 1100px; margin: 0 auto; }
  .rt-card {
    background: transparent; border-radius: 24px;
    padding: 60px; 
  }
  
  .rt-section { margin-bottom: 48px; }
  .rt-section:last-child { margin-bottom: 0; }
  
  .rt-h2 { font-family: var(--rt-ff-head); font-size: 28px; font-weight: 800; color: var(--rt-ink); margin-bottom: 20px; letter-spacing: -.01em; }
  .rt-p { font-size: 17px; color: var(--rt-ink2); margin-bottom: 24px; line-height: 1.75; }
  
  .rt-list { list-style: none; padding: 0; margin: 0 0 24px 0; }
  .rt-list-item { 
    position: relative; padding-left: 28px; margin-bottom: 16px; font-size: 17px; color: var(--rt-ink2); 
    display: flex; flex-direction: column;
  }
  .rt-list-item::before {
    content: '→'; position: absolute; left: 0; color: var(--rt-mint); font-weight: 700;
  }
  .rt-list-label { font-weight: 700; color: var(--rt-ink); margin-bottom: 4px; }

  @media (max-width: 768px) {
    .rt-h1 { font-size: 44px; }
    .rt-card { padding: 40px 24px; }
    .rt-h2 { font-size: 24px; }
  }
`;

function RefTerms() {
  const { t } = useTranslation('terms');

  return (
    <div className="rt-page">
      <style>{TERMS_STYLES}</style>

      {/* HERO */}
      <div className="rt-hero">
        <div className="rt-hero-content">
          <div className="rt-eyebrow">
            <span className="rt-eyebrow-dot" /> {t('hero.eyebrow')}
          </div>
          <h1 className="rt-h1">
            {t('hero.titlePrefix')}<span className="rt-h1-grad">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="rt-last-updated">{t('hero.lastUpdated')}</p>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="rt-content-wrap">
        <div className="rt-card">
          <div className="rt-section">
            <p className="rt-p">
              {t('intro')}
            </p>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('services.title')}</h2>
            <p className="rt-p">
              {t('services.description')}
            </p>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('responsibilities.title')}</h2>
            <div className="rt-list">
              <div className="rt-list-item">
                <span className="rt-list-label">{t('responsibilities.accountLabel')}</span>
                {t('responsibilities.accountDesc')}
              </div>
              <div className="rt-list-item">
                <span className="rt-list-label">{t('responsibilities.accuracyLabel')}</span>
                {t('responsibilities.accuracyDesc')}
              </div>
              <div className="rt-list-item">
                <span className="rt-list-label">{t('responsibilities.complianceLabel')}</span>
                {t('responsibilities.complianceDesc')}
              </div>
            </div>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('payment.title')}</h2>
            <div className="rt-list">
              <div className="rt-list-item">
                <span className="rt-list-label">{t('payment.pricingLabel')}</span>
                {t('payment.pricingDesc')}
              </div>
              <div className="rt-list-item">
                <span className="rt-list-label">{t('payment.billingLabel')}</span>
                {t('payment.billingDesc')}
              </div>
              <div className="rt-list-item">
                <span className="rt-list-label">{t('payment.refundsLabel')}</span>
                {t('payment.refundsDesc')}
              </div>
            </div>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('privacy.title')}</h2>
            <p className="rt-p">
              {t('privacy.description')}
            </p>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('use.title')}</h2>
            <div className="rt-list">
              <div className="rt-list-item">
                <span className="rt-list-label">{t('use.prohibitedLabel')}</span>
                {t('use.prohibitedDesc')}
              </div>
              <div className="rt-list-item">
                <span className="rt-list-label">{t('use.terminationLabel')}</span>
                {t('use.terminationDesc')}
              </div>
            </div>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('ip.title')}</h2>
            <p className="rt-p">
              {t('ip.description')}
            </p>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('liability.title')}</h2>
            <p className="rt-p">
              {t('liability.description')}
            </p>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('governing.title')}</h2>
            <p className="rt-p">
              {t('governing.description')}
            </p>
          </div>

          <div className="rt-section">
            <h2 className="rt-h2">{t('contact.title')}</h2>
            <p className="rt-p">
              {t('contact.pPrefix')}<strong>{t('contact.email')}</strong>{t('contact.pSuffix')}
            </p>
          </div>
        </div>
      </div>

      <RefSiteFooter />
    </div>
  );
}

export default RefTerms;
